'use client';

// ---------------------------------------------------------------------------
// LocalePageRenderer.tsx — Client component that renders the correct page
// ---------------------------------------------------------------------------
// Resolves a URL slug to the appropriate marketing page component.
// Sets the Zustand locale store so all useTranslation() + usePageContent()
// calls render in the correct language.
// ---------------------------------------------------------------------------

import { useMemo } from 'react';
import { notFound } from 'next/navigation';
import { useLocaleStore } from '../locale.store';
import { LocaleContext } from '../locale.context';
import { isSupportedLocale, type SupportedLocale } from '../i18n.config';
import {
  FeaturesPage,
  PricingPage,
  BlogIndex,
  BlogPost,
  PartnerProgramPage,
  IntegrationsHub,
  ComplianceHub,
  CitiesHub,
  CityLanding,
  CITY_CONFIGS,
  ComparisonPage,
  COMPARISON_CONFIGS,
  IntegrationLanding,
  INTEGRATION_CONFIGS,
  ComplianceLanding,
  COMPLIANCE_CONFIGS,
  SEOProductLanding,
  IndustryLanding,
  CountryLanding,
  COUNTRY_CONFIGS,
  BestSoftwareRanking,
  BEST_SOFTWARE_CONFIG,
  ForHotelsPage,
} from '@/modules/hotels';
import { BLOG_POSTS } from '@/modules/hotels/components/blog.data';
import {
  AboutPage,
  ContactPage,
  SecurityPage,
  DemosPage,
  PrivacyPolicy,
  TermsOfService,
  RefundPolicy,
  SecurityWhitepaperPage,
  CaseStudiesPage,
} from '@/modules/legal';

// Lazy-loaded configs from extended data files
import { INDUSTRY_CONFIGS } from '@/modules/hotels/components/seo-industry.data';
import { SEO_LANDING_CONFIGS } from '@/modules/hotels/components/seo-landing.data';

// ---------------------------------------------------------------------------
// Locale setter hook — sets Zustand store on mount for locale routes
// ---------------------------------------------------------------------------

function useSetLocale(locale: string) {
  useMemo(() => {
    if (isSupportedLocale(locale)) {
      useLocaleStore.setState({ locale: locale as SupportedLocale });
    }
  }, [locale]);
}

// ---------------------------------------------------------------------------
// LocaleHomePage — renders the home page for a locale route (e.g. /ar)
// ---------------------------------------------------------------------------

export function LocaleHomePage({ locale }: { locale: string }) {
  useSetLocale(locale);
  const safeLocale = isSupportedLocale(locale) ? locale : ('en' as SupportedLocale);
  return (
    <LocaleContext.Provider value={safeLocale}>
      <ForHotelsPage />
    </LocaleContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// LocalePageRenderer — catch-all for locale slug pages
// ---------------------------------------------------------------------------

interface LocalePageRendererProps {
  slug: string;
  segments: string[];
  locale: string;
}

export function LocalePageRenderer({ slug, segments, locale }: LocalePageRendererProps) {
  useSetLocale(locale);
  const safeLocale = isSupportedLocale(locale) ? locale : ('en' as SupportedLocale);

  return (
    <LocaleContext.Provider value={safeLocale}>
      <LocaleRouteResolver slug={slug} segments={segments} />
    </LocaleContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Route resolver — extracted from LocalePageRenderer for context wrapping
// ---------------------------------------------------------------------------

function LocaleRouteResolver({ slug, segments }: { slug: string; segments: string[] }) {
  // Integration sub-pages: integrations/<name>
  if (segments[0] === 'integrations' && segments[1]) {
    const integrationKey = segments[1] === 'razorpay' ? 'paymenthooks' : segments[1];
    const cfg = INTEGRATION_CONFIGS[integrationKey];
    return cfg ? <IntegrationLanding slug={integrationKey} /> : notFound();
  }

  // Compliance sub-pages: compliance/<region>
  if (segments[0] === 'compliance' && segments[1]) {
    const cfg = COMPLIANCE_CONFIGS[segments[1]];
    return cfg ? <ComplianceLanding config={cfg} /> : notFound();
  }

  // Hub pages
  if (slug === 'features') return <FeaturesPage />;
  if (slug === 'pricing') return <PricingPage />;
  if (slug === 'blog') return <BlogIndex />;

  // Blog posts: /<locale>/blog/<slug>
  if (segments[0] === 'blog' && segments[1]) {
    const post = BLOG_POSTS.find((p) => p.slug === segments[1]);
    if (post) return <BlogPost post={post} />;
    return notFound();
  }

  if (slug === 'partner-program') return <PartnerProgramPage />;
  if (slug === 'hotel-integrations') return <IntegrationsHub />;
  if (slug === 'hotel-compliance') return <ComplianceHub />;
  if (slug === 'hotel-cities') return <CitiesHub />;

  // City landing pages (hotel-software-dubai, etc.)
  if (slug.startsWith('hotel-software-') && !slug.startsWith('hotel-software-for-')) {
    return CITY_CONFIGS[slug] ? <CityLanding slug={slug} /> : notFound();
  }

  // Comparison pages (hotelstack-vs-*)
  if (slug.startsWith('hotelstack-vs-')) {
    const cfg = COMPARISON_CONFIGS[slug];
    return cfg ? <ComparisonPage configMap={cfg} /> : notFound();
  }

  // Industry pages (hotel-software-for-*)
  if (slug.startsWith('hotel-software-for-')) {
    return INDUSTRY_CONFIGS?.[slug] ? <IndustryLanding slug={slug} /> : notFound();
  }

  // SEO product pages
  const seoProducts = [
    'hotel-management-software', 'hotel-pms-software', 'hotel-booking-engine',
    'hotel-channel-manager', 'hotel-crm', 'hotel-revenue-management',
  ];
  if (seoProducts.includes(slug)) {
    return SEO_LANDING_CONFIGS?.[slug] ? <SEOProductLanding slug={slug} /> : notFound();
  }

  // Country landing pages (hotel-management-software-us, etc.)
  if (slug.startsWith('hotel-management-software-')) {
    const countryKey = slug.replace('hotel-management-software-', '');
    return COUNTRY_CONFIGS[countryKey] ? <CountryLanding slug={slug} /> : notFound();
  }

  // Best software ranking
  if (slug.startsWith('best-hotel-management-software')) {
    return BEST_SOFTWARE_CONFIG ? <BestSoftwareRanking config={BEST_SOFTWARE_CONFIG} /> : notFound();
  }

  // Case studies
  if (slug === 'case-studies') return <CaseStudiesPage />;

  // Standalone pages from the legal module
  switch (slug) {
    case 'about': return <AboutPage />;
    case 'contact': return <ContactPage />;
    case 'security': return <SecurityPage />;
    case 'security-whitepaper': return <SecurityWhitepaperPage />;
    case 'demos': return <DemosPage />;
    case 'privacy': return <PrivacyPolicy />;
    case 'terms': return <TermsOfService />;
    case 'refund-policy': return <RefundPolicy />;
    default: break;
  }

  // Hotels in Delhi (special case)
  if (slug === 'hotels-in-delhi') {
    return <CityLanding slug="hotels-in-delhi" />;
  }

  // Register hotel page
  if (slug === 'register-hotel') {
    const { HotelRegistrationWizard } = require('@/modules/onboarding');
    return <HotelRegistrationWizard />;
  }

  return notFound();
}
